//@flow
import * as Immutable from 'immutable';

import { CreateDistributionPlanConfig } from 'common/models/flow/CreateDistributionPlanConfig';
import type { CreateDistributionPlanConfigModelType } from 'common/models/flow/CreateDistributionPlanConfig';

import { SalesCategoryGroup } from './SalesCategoryGroup';
import I18nHelper from 'common/helpers/I18nHelper';
import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import {
    validate,
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalCreateDistributionPlanConfig extends CreateDistributionPlanConfig {
    static fromJS(
        json: CreateDistributionPlanConfigModelType
    ): InternalCreateDistributionPlanConfig {
        const state: Object = Object.assign({}, json);
        state.salesCategoriesList = Immutable.List(state.salesCategoriesList);
        state.salesmanCategoryGroups = state.salesmanCategoryGroups
            ? Immutable.Map(state.salesmanCategoryGroups).map(item =>
                  SalesCategoryGroup.fromJS(item)
              )
            : state.salesmanCategoryGroups;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const salesCategories = this.getSalesCategoriesList();
        if (this.getSalesCategoryOptions(salesCategories).size !== 0) {
            return true;
        }
        return false;
    }

    getSalesCategoryOptions(salesCategories) {
        const { salesmanCategoryGroups } = this;

        const assignedSalesCategories = salesmanCategoryGroups
            .valueSeq()
            .map(group => group.categories)
            .toJS()
            .flat();

        const assignableSalesCategories = salesCategories?.filter(
            ({ value }) => !assignedSalesCategories.includes(value)
        );
        return assignableSalesCategories;
    }

    @validate
    setSalesmanCategoryGroups(salesmanCategoryGroups: ?Object): this {
        return super.setSalesmanCategoryGroups(salesmanCategoryGroups);
    }

    validateSalesmanCategoryGroups() {
        const salesCategories = this.getSalesCategoriesList();
        if (
            !this.salesmanCategoryGroups?.size ||
            this.salesmanCategoryGroups.some(
                ({ categories }) => !categories.size
            )
        ) {
            return nsTranslate('pjpValidationErrors.emptySalesCategoriesError');
        } else if (this.getSalesCategoryOptions(salesCategories).size) {
            return nsTranslate('pjpValidationErrors.unassignedSalesCategories');
        } else {
            return null;
        }
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    @validate
    setDistributionOffset(distributionOffset: ?number): this {
        return super.setDistributionOffset(distributionOffset);
    }

    validateDistributionOffset(): ?string {
        const { distributionOffset } = this;
        if (
            DataUtils.isEmpty(distributionOffset) ||
            !NumberUtils.isInteger(distributionOffset) ||
            NumberUtils.toNumber(distributionOffset) < 0
        ) {
            return nsTranslate('pjpValidationErrors.numberOfDaysError');
        }
    }

    @validate
    setSalesmanCategoryGroupCapacities(
        salesmanCategoryGroupCapacities: Object
    ): this {
        return super.setSalesmanCategoryGroupCapacities(
            salesmanCategoryGroupCapacities
        );
    }

    validateSalesmanCategoryGroupCapacities(): ?string {
        if (
            !this.salesmanCategoryGroupCapacities ||
            !this.salesmanCategoryGroupCapacities.size ||
            this.salesmanCategoryGroupCapacities.some(capacity => !capacity)
        ) {
            return nsTranslate('pjpValidationErrors.capacityError');
        }
    }

    setSalesCategoriesList(salesCategoriesList): this {
        return super.setSalesCategoriesList(salesCategoriesList);
    }

    getSalesCategoriesList() {
        return this.get('salesCategoriesList') || Immutable.List();
    }
}

export { InternalCreateDistributionPlanConfig as CreateDistributionPlanConfig };
